import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { GenericResponse } from "./APIInterfaces";
import { IUser } from "Models/User";
import { loggedUserApi } from "./loggedUserApi";
import { LoginInput } from "pages/auth";
import CryptoJS from 'crypto-js'

export interface IVerifyAccountRequest {
  userId: string;
  password: string;
};

export interface IChangePasswordRequest {
  userId: string;
  password: string;
  currentPassword?: string;
};

export interface IResetPasswordRequest {
  resetToken: string;
  password: string;
  passwordConfirm: string;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    registerUser: builder.mutation<GenericResponse, void>({
      query(data) {
        return {
          url: "auth/register",
          method: "POST",
          body: data,
        };
      },
    }),
    loginUser: builder.mutation<
      { access_token: string; status: string },
      LoginInput
    >({
      query(data) {
        return {
          url: "auth/admin",
          method: "POST",
          body: data,
          credentials: "include",
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          await dispatch(loggedUserApi.endpoints.getMe.initiate(null));
        } catch (error) {}
      },
    }),
    logoutUser: builder.mutation<void, void>({
      query() {
        return {
          url: "auth/logout",
          credentials: "include",
        };
      },
    }),
    verifyEmail: builder.mutation<GenericResponse, string>({
      query(verificationCode) {
        return {
          url: `auth/verifyemail/${verificationCode}`,
          credentials: "include",
        };
      },
    }),
    forgotPassword: builder.mutation<GenericResponse, { email: string }>({
      query(body) {
        return {
          url: `auth/forgotpassword`,
          method: "POST",
          credentials: "include",
          body,
        };
      },
    }),
    resetPassword: builder.mutation<GenericResponse, IResetPasswordRequest>({
      query({ resetToken, password, passwordConfirm }) {
        return {
          url: `auth/resetpassword/${resetToken}`,
          method: "PATCH",
          body: { password, passwordConfirm },
          credentials: "include",
        };
      },
    }),
    changePassword: builder.mutation<GenericResponse, IChangePasswordRequest>({
      query({ userId, password, currentPassword }) {

        const jsonToEncrypt = { password, currentPassword, userId };

        const data = CryptoJS.AES.encrypt(JSON.stringify(jsonToEncrypt), 'user password').toString();

        return {
          url: `auth/set-password/${userId}`,
          method: "PUT",
          body: { password: data },
          credentials: "include",
        };
      },
    }),
    getUserByVerificationCode: builder.query<IUser, string>({
      query(verificationCode) {
        return {
          url: `auth/` + verificationCode,
          method: "GET",
          credentials: "include",
        };
      },
      transformResponse: (results: { status: string; data: IUser }) =>
        results.data,
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useLogoutUserMutation,
  useVerifyEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetUserByVerificationCodeQuery,
  useChangePasswordMutation
} = authApi;
